import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Collapse,
  Alert,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';
import ArticleIcon from '@mui/icons-material/Article';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SailingIcon from '@mui/icons-material/Sailing';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import FolderIcon from '@mui/icons-material/Folder';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import { getAuthToken, isAuthenticated } from './utils/auth';
import { useAuth } from './contexts/AuthContext';
import UserManagement from './components/UserManagement';
import NewsletterManagement from './components/NewsletterManagement';
import ImageGallery from './components/ImageGallery';
import SailthruHealthcheck from './components/SailthruHealthcheck';
import SailthruLists from './components/SailthruLists';
import SailthruUsers from './components/SailthruUsers';

const drawerWidth = 300;

const tabLabels = [
  'TEMPLATE & AUDIENCE',
  'SUBJECT',
  'HEADER',
  'FOOTER',
  'CONTENT',
  'ADS',
  'SCHEDULE',
];

interface User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
}

interface Folder {
  id: string;
  name: string;
  type: 'year' | 'month' | 'week' | 'custom';
  parentId?: string;
  path: string;
  createdAt: string;
  newsletters: Newsletter[];
  subfolders: Folder[];
  isExpanded?: boolean;
}

interface Newsletter {
  id: string;
  title: string;
  status: string;
  author: string;
  createdAt: string;
  updatedAt: string;
  folderId?: string;
}

interface DashboardProps {
  themeMode: 'light' | 'dark';
  toggleTheme: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ themeMode, toggleTheme }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [selectedSection, setSelectedSection] = useState('newsletters');
  const [newsletterEditMode, setNewsletterEditMode] = useState(false);
  const [newsletterTab, setNewsletterTab] = useState(0);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Folder structure state
  const [folders, setFolders] = useState<Folder[]>([]);
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);

  // Menu and dialog state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createFolderDialog, setCreateFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [parentFolderId, setParentFolderId] = useState<string | null>(null);
  const [deleteFolderDialog, setDeleteFolderDialog] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<Folder | null>(null);
  const [renameFolderDialog, setRenameFolderDialog] = useState(false);
  const [folderToRename, setFolderToRename] = useState<Folder | null>(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [sailthruExpanded, setSailthruExpanded] = useState(false);
  const theme = useTheme();

  const fetchCurrentUser = useCallback(async () => {
    try {
      const token = getAuthToken();

      // Decode JWT to get user info
      const payload = JSON.parse(atob(token!.split('.')[1]));
      setCurrentUser({
        id: payload.id || payload.username, // Use username as fallback for id
        username: payload.username,
        firstName: payload.firstName || 'User',
        lastName: payload.lastName || 'Name',
        role: payload.role || 'user',
        isActive: payload.isActive !== false
      });
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  const fetchFolders = useCallback(async () => {
    try {
      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(`${apiUrl}/prod/newsletter-folders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch folders');
      const data = await response.json();

      // Preserve existing expansion state when refreshing folders
      const preserveExpansionState = (folderList: Folder[], existingFolders: Folder[] = []): Folder[] => {
        return folderList.map(folder => {
          // Find existing folder to preserve its expansion state
          const existingFolder = existingFolders.find(f => f.id === folder.id);
          return {
            ...folder,
            isExpanded: existingFolder ? existingFolder.isExpanded : false,
            subfolders: folder.subfolders ? preserveExpansionState(folder.subfolders, existingFolder?.subfolders || []) : []
          };
        });
      };

      setFolders(prevFolders => {
        const foldersWithExpansion = preserveExpansionState(data.folders || [], prevFolders);
        return foldersWithExpansion;
      });
    } catch (err: any) {
      console.error('Error fetching folders:', err);
    }
  }, []);

  useEffect(() => {
    // Check authentication and get current user
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    fetchCurrentUser();
    fetchFolders();
    fetchNewsletters(); // Fetch all newsletters initially
  }, [navigate, fetchCurrentUser, fetchFolders]);

  const fetchNewsletters = async (folderId?: string | null) => {
    try {
      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      // Build URL with folderId query parameter if provided
      let url = `${apiUrl}/prod/newsletter-management`;
      if (folderId) {
        url += `?folderId=${folderId}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch newsletters');
      const data = await response.json();
      setNewsletters(data.newsletters || []);
    } catch (err: any) {
      console.error('Error fetching newsletters:', err);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleBackToList = () => {
    setNewsletterEditMode(false);
  };


  const toggleFolderExpansion = (folderId: string) => {
    const updateFolderExpansion = (folderList: Folder[]): Folder[] => {
      return folderList.map(folder => {
        if (folder.id === folderId) {
          return { ...folder, isExpanded: !folder.isExpanded };
        }
        if (folder.subfolders.length > 0) {
          return { ...folder, subfolders: updateFolderExpansion(folder.subfolders) };
        }
        return folder;
      });
    };
    const updatedFolders = updateFolderExpansion(folders);
    setFolders(updatedFolders);
  };

  const handleFolderSelect = (folderId: string) => {
    setSelectedFolder(folderId);
    setSelectedSection('newsletters');

    // Fetch newsletters for this specific folder
    fetchNewsletters(folderId);
  };


  const handleNewsletterCreated = () => {
    // Refresh newsletters for the currently selected folder
    fetchNewsletters(selectedFolder);
  };

  const getFolderName = (folderId: string): string | null => {
    const findFolder = (folderList: Folder[]): Folder | null => {
      for (const folder of folderList) {
        if (folder.id === folderId) {
          return folder;
        }
        if (folder.subfolders && folder.subfolders.length > 0) {
          const found = findFolder(folder.subfolders);
          if (found) return found;
        }
      }
      return null;
    };

    const folder = findFolder(folders);
    return folder ? folder.name : null;
  };


  const getFolderIcon = (type: string) => {
    switch (type) {
      case 'year':
        return <CalendarMonthIcon />;
      case 'month':
        return <DateRangeIcon />;
      case 'week':
        return <DateRangeIcon />;
      default:
        return <FolderIcon />;
    }
  };

  const getFolderNewsletters = (folderId: string): Newsletter[] => {
    return newsletters.filter(n => n.folderId === folderId);
  };


  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, parentId?: string, folder?: Folder) => {
    setAnchorEl(event.currentTarget);
    setParentFolderId(parentId || null);
    setFolderToDelete(folder || null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setParentFolderId(null);
  };

  const handleCreateFolderClick = () => {
    setCreateFolderDialog(true);
    setAnchorEl(null); // Close menu but don't clear parentFolderId yet
  };

  const handleDeleteFolderClick = () => {
    setDeleteFolderDialog(true);
    handleMenuClose();
  };

  const handleRenameFolderClick = () => {
    if (folderToDelete) {
      setFolderToRename(folderToDelete);
      setRenameFolderName(folderToDelete.name);
      setRenameFolderDialog(true);
    }
    handleMenuClose();
  };

  const deleteFolder = async () => {
    if (!folderToDelete) return;

    try {
      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(`${apiUrl}/prod/newsletter-folders/${folderToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete folder');
      }

      setDeleteFolderDialog(false);
      setFolderToDelete(null);
      fetchFolders();
    } catch (err: any) {
      alert(`Error deleting folder: ${err.message}`);
    }
  };

  const renameFolder = async () => {
    if (!folderToRename || !renameFolderName.trim()) return;

    try {
      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(`${apiUrl}/prod/newsletter-folders/${folderToRename.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: renameFolderName.trim(),
          updatedBy: currentUser?.username
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to rename folder');
      }

      setRenameFolderDialog(false);
      setFolderToRename(null);
      setRenameFolderName('');
      fetchFolders();
    } catch (err: any) {
      alert(`Error renaming folder: ${err.message}`);
    }
  };

  const createFolder = async () => {
    try {
      const token = getAuthToken();
      const apiUrl = process.env.REACT_APP_API_URL;

      const requestBody = {
        name: newFolderName,
        type: 'custom',
        parentId: parentFolderId
      };

      const response = await fetch(`${apiUrl}/prod/newsletter-folders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) throw new Error('Failed to create folder');

      setCreateFolderDialog(false);
      setNewFolderName('');
      setParentFolderId(null);
      setFolderToDelete(null);
      fetchFolders();
    } catch (err: any) {
      alert(`Error creating folder: ${err.message}`);
    }
  };

  const renderFolderTree = (folderList: Folder[], level: number = 0) => {
    return folderList.map((folder) => (
      <React.Fragment key={folder.id}>
        <ListItem
          button
          onClick={() => handleFolderSelect(folder.id)}
          sx={{
            pl: 2 + level * 2,
            color: theme.palette.text.primary,
            '&:hover': {
              bgcolor: theme.palette.action.hover
            },
            bgcolor: selectedFolder === folder.id ? theme.palette.action.selected : 'transparent'
          }}
        >
          <ListItemIcon sx={{ color: theme.palette.text.primary, minWidth: 32 }}>
            {folder.subfolders.length > 0 ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolderExpansion(folder.id);
                }}
              >
                {folder.isExpanded ? <KeyboardArrowDownIcon /> : <ChevronRightIcon />}
              </IconButton>
            ) : (
              <Box sx={{ width: 32 }} />
            )}
          </ListItemIcon>
          <ListItemIcon sx={{ color: theme.palette.text.primary, minWidth: 32 }}>
            {getFolderIcon(folder.type)}
          </ListItemIcon>
          <Tooltip title={folder.name} placement="right">
            <ListItemText
              primary={folder.name}
              sx={{
                '& .MuiListItemText-primary': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '150px'
                }
              }}
            />
          </Tooltip>
          <ListItemSecondaryAction sx={{ right: 8 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClick(e, folder.id, folder);
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
        {folder.subfolders.length > 0 && (
          <Collapse in={folder.isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {renderFolderTree(folder.subfolders, level + 1)}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    ));
  };

  const renderNewsletterTabs = () => (
    <Box sx={{ background: theme.palette.background.paper, borderRadius: 2, boxShadow: 2, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackToList} sx={{ mr: 2, color: theme.palette.text.primary, bgcolor: theme.palette.background.default, '&:hover': { bgcolor: theme.palette.action.hover } }}>
          Back to List
        </Button>
        <Tabs
          value={newsletterTab}
          onChange={(_, v) => setNewsletterTab(v)}
          textColor="inherit"
          TabIndicatorProps={{ style: { background: '#b71c1c', height: 4, borderRadius: 2 } }}
          sx={{
            '.MuiTab-root': {
              minWidth: 120,
              fontWeight: 500,
              color: theme.palette.text.primary,
              letterSpacing: 2,
              fontSize: 16,
            },
            '.Mui-selected': {
              color: '#b71c1c',
            },
          }}
        >
          {tabLabels.map((label, idx) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Box>
      <Box sx={{ minHeight: 200, p: 2 }}>
        <Typography variant="h6" sx={{ color: '#b71c1c', mb: 2 }}>
          {tabLabels[newsletterTab]}
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
          Content for {tabLabels[newsletterTab]} goes here.
        </Typography>
      </Box>
    </Box>
  );

  const renderContent = () => {
    if (selectedSection === 'users') {
      return currentUser ? (
        <UserManagement currentUser={currentUser} themeMode={themeMode} />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <Typography>Loading user data...</Typography>
        </Box>
      );
    }

    if (selectedSection === 'files') {
      return (
        <ImageGallery
          themeMode={themeMode}
          onSelectImage={() => {
            // You can add logic here to handle image selection
          }}
        />
      );
    }

    if (selectedSection === 'sailthru-healthcheck') {
      return <SailthruHealthcheck />;
    }

    if (selectedSection === 'sailthru-lists') {
      return <SailthruLists />;
    }

    if (selectedSection === 'sailthru-users') {
      return <SailthruUsers />;
    }

    if (newsletterEditMode) {
      return renderNewsletterTabs();
    }

    if (selectedSection === 'newsletters') {
      return (
        <Box>
          <NewsletterManagement
            currentUser={currentUser}
            themeMode={themeMode}
            filteredNewsletters={newsletters}
            selectedFolder={selectedFolder}
            selectedFolderName={selectedFolder ? getFolderName(selectedFolder) : null}
            onNewsletterCreated={handleNewsletterCreated}
          />
        </Box>
      );
    }

    return <NewsletterManagement currentUser={currentUser} themeMode={themeMode} />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: theme.palette.background.default }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: themeMode === "light" ? "#d32f2f" : theme.palette.background.paper,
        }}
      >
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            <span
              style={{
                fontFamily: "monospace",
                fontWeight: 900,
                fontSize: 24,
                color: themeMode === "light" ? "white" : "#d32f2f",
              }}
            >
              M@ES
            </span>
          </Typography>
          <IconButton
            color="inherit"
            onClick={toggleTheme}
            sx={{
              mr: 2,
              color: themeMode === "light" ? "white" : "inherit",
            }}
          >
            {themeMode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          <Button
            color="inherit"
            onClick={handleLogout}
            sx={{
              color: themeMode === "light" ? "white" : "inherit",
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            background: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`,
            mt: "64px",
          },
        }}
      >
        <List>
          <ListItem
            button
            selected={selectedSection === "newsletters"}
            onClick={() => {
              setSelectedSection("newsletters");
              setSelectedFolder(null);
              fetchNewsletters(); // Fetch all newsletters without folderId
            }}
            sx={{
              color: theme.palette.text.primary,
              "&.Mui-selected": {
                bgcolor: "#d32f2f",
                "&:hover": { bgcolor: "#b71c1c" },
              },
            }}
          >
            <ListItemIcon sx={{ color: theme.palette.text.primary }}>
              <ArticleIcon />
            </ListItemIcon>
            <ListItemText primary="Newsletters" />
            <ListItemSecondaryAction>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuClick(e);
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>

          {/* Newsletter Folder Structure */}
          <List component="div" disablePadding>
            {/* Folder Tree */}
            {renderFolderTree(folders)}
          </List>
          <ListItem
            button
            selected={selectedSection === "users"}
            onClick={() => setSelectedSection("users")}
            sx={{
              color: theme.palette.text.primary,
              "&.Mui-selected": {
                bgcolor: "#d32f2f",
                "&:hover": { bgcolor: "#b71c1c" },
              },
            }}
          >
            <ListItemIcon sx={{ color: theme.palette.text.primary }}>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary={currentUser?.role === "admin" ? "Users" : "My Profile"} />
          </ListItem>
          <ListItem
            button
            selected={selectedSection === "files"}
            onClick={() => setSelectedSection("files")}
            sx={{
              color: theme.palette.text.primary,
              "&.Mui-selected": {
                bgcolor: "#d32f2f",
                "&:hover": { bgcolor: "#b71c1c" },
              },
            }}
          >
            <ListItemIcon sx={{ color: theme.palette.text.primary }}>
              <FolderOpenIcon />
            </ListItemIcon>
            <ListItemText primary="File Manager" />
          </ListItem>
          <ListItem
            button
            onClick={() => setSailthruExpanded(!sailthruExpanded)}
            sx={{
              color: theme.palette.text.primary,
            }}
          >
            <ListItemIcon sx={{ color: theme.palette.text.primary }}>
              <SailingIcon />
            </ListItemIcon>
            <ListItemText primary="SailThru" />
            {sailthruExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={sailthruExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                button
                selected={selectedSection === "sailthru-healthcheck"}
                onClick={() => setSelectedSection("sailthru-healthcheck")}
                sx={{
                  pl: 4,
                  color: theme.palette.text.primary,
                  "&.Mui-selected": {
                    bgcolor: "#d32f2f",
                    "&:hover": { bgcolor: "#b71c1c" },
                  },
                }}
              >
                <ListItemIcon sx={{ color: theme.palette.text.primary }}>
                  <MonitorHeartIcon />
                </ListItemIcon>
                <ListItemText primary="Healthcheck" />
              </ListItem>
              <ListItem
                button
                selected={selectedSection === 'sailthru-lists'}
                onClick={() => setSelectedSection('sailthru-lists')}
                sx={{
                  pl: 4,
                  color: theme.palette.text.primary,
                  '&.Mui-selected': {
                    bgcolor: '#d32f2f',
                    '&:hover': { bgcolor: '#b71c1c' }
                  }
                }}
              >
                <ListItemIcon sx={{ color: theme.palette.text.primary }}>
                  <ArticleIcon />
                </ListItemIcon>
                <ListItemText primary="Smartlists" />
              </ListItem>
              <ListItem
                button
                selected={selectedSection === 'sailthru-users'}
                onClick={() => setSelectedSection('sailthru-users')}
                sx={{
                  pl: 4,
                  color: theme.palette.text.primary,
                  '&.Mui-selected': {
                    bgcolor: '#d32f2f',
                    '&:hover': { bgcolor: '#b71c1c' }
                  }
                }}
              >
                <ListItemIcon sx={{ color: theme.palette.text.primary }}>
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText primary="Users" />
              </ListItem>
            </List>
          </Collapse>
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: "64px" }}>
        {renderContent()}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleCreateFolderClick}>
          <ListItemIcon>
            <CreateNewFolderIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create {parentFolderId ? "Subfolder" : "Folder"}</ListItemText>
        </MenuItem>
        {folderToDelete && (
          <MenuItem onClick={handleRenameFolderClick}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rename Folder</ListItemText>
          </MenuItem>
        )}
        {folderToDelete && (
          <MenuItem onClick={handleDeleteFolderClick}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Folder</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Create Folder Dialog */}
      <Dialog
        open={createFolderDialog}
        onClose={() => {
          setCreateFolderDialog(false);
          setParentFolderId(null);
          setFolderToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create {parentFolderId ? "Subfolder" : "Folder"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateFolderDialog(false);
              setParentFolderId(null);
              setFolderToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={createFolder}
            variant="contained"
            disabled={!newFolderName.trim()}
            sx={{
              bgcolor: themeMode === "light" ? "#d32f2f" : "#f44336",
              "&:hover": { bgcolor: themeMode === "light" ? "#b71c1c" : "#d32f2f" },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Folder Dialog */}
      <Dialog open={deleteFolderDialog} onClose={() => setDeleteFolderDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Folder</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the folder "{folderToDelete?.name}"?</Typography>
          {folderToDelete && getFolderNewsletters(folderToDelete.id).length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This folder contains {getFolderNewsletters(folderToDelete.id).length} newsletter(s). You cannot delete a
              folder that contains newsletters.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFolderDialog(false)}>Cancel</Button>
          <Button
            onClick={deleteFolder}
            variant="contained"
            color="error"
            disabled={folderToDelete ? getFolderNewsletters(folderToDelete.id).length > 0 : false}
            sx={{
              bgcolor: "#d32f2f",
              "&:hover": { bgcolor: "#b71c1c" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog
        open={renameFolderDialog}
        onClose={() => {
          setRenameFolderDialog(false);
          setFolderToRename(null);
          setRenameFolderName("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rename Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={renameFolderName}
            onChange={(e) => setRenameFolderName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRenameFolderDialog(false);
              setFolderToRename(null);
              setRenameFolderName("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={renameFolder}
            variant="contained"
            disabled={!renameFolderName.trim()}
            sx={{
              bgcolor: themeMode === "light" ? "#d32f2f" : "#f44336",
              "&:hover": { bgcolor: themeMode === "light" ? "#b71c1c" : "#d32f2f" },
            }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
